/**
 * Gallery sample data (U5). PREVIEW CONTENT ONLY — figures below play the
 * role of a contractor's OWN library costs. Job Site AI never ships or
 * suggests market pricing (product law: no invented market prices), and the
 * gallery labels itself accordingly.
 */
import type { LineGridSuggestion } from '../LineItemGrid';

export interface SampleLine {
  id: string;
  description: string;
  qty: number | null;
  unit: string;
  unitCost: number | null;
  /** Precomputed preview figure — production totals come from the shared engine (U3). */
  extended: number | null;
  tone?: 'alternate' | 'allowance';
}

export const SAMPLE_LINES: SampleLine[] = [
  {
    id: 'l1',
    description: 'Journeyman electrician — rough-in',
    qty: 32,
    unit: 'hr',
    unitCost: 78,
    extended: 2496,
  },
  {
    id: 'l2',
    description: 'Apprentice electrician — rough-in',
    qty: 32,
    unit: 'hr',
    unitCost: 52,
    extended: 1664,
  },
  {
    id: 'l3',
    description: '12/2 NM-B copper wire',
    qty: 750,
    unit: 'ft',
    unitCost: 0.48,
    extended: 360,
  },
  {
    id: 'l4',
    description: '20A duplex receptacle, tamper-resistant',
    qty: 24,
    unit: 'ea',
    unitCost: 3.1,
    extended: 74.4,
  },
  {
    id: 'l5',
    description: 'Single-gang nail-on box',
    qty: 30,
    unit: 'ea',
    unitCost: 0.85,
    extended: 25.5,
  },
  {
    id: 'l6',
    description: 'Panel makeup + breakers — allowance',
    qty: 1,
    unit: 'ls',
    unitCost: 850,
    extended: 850,
    tone: 'allowance',
  },
  {
    id: 'l7',
    description: 'Under-cabinet LED lighting (alternate)',
    qty: 18,
    unit: 'ft',
    unitCost: 24,
    extended: 432,
    tone: 'alternate',
  },
];

/** Type-ahead catalog for the description column (library/assembly picker). */
const CATALOG: LineGridSuggestion[] = [
  { id: 'lr1', label: 'Journeyman electrician', detail: '$78.00 / hr', group: 'Labor roles' },
  { id: 'lr2', label: 'Apprentice electrician', detail: '$52.00 / hr', group: 'Labor roles' },
  { id: 'lr3', label: 'Foreman electrician', detail: '$94.50 / hr', group: 'Labor roles' },
  { id: 'm1', label: '12/2 NM-B copper wire', detail: '$0.48 / ft', group: 'Materials' },
  { id: 'm2', label: '12/3 NM-B copper wire', detail: '$0.72 / ft', group: 'Materials' },
  {
    id: 'm3',
    label: '20A duplex receptacle, tamper-resistant',
    detail: '$3.10 / ea',
    group: 'Materials',
  },
  { id: 'm4', label: 'Single-gang nail-on box', detail: '$0.85 / ea', group: 'Materials' },
  { id: 'm5', label: '4" square junction box', detail: '$1.60 / ea', group: 'Materials' },
  { id: 'a1', label: 'Recessed can — install', detail: '0.35 hr/ea · costs from your library', group: 'Assemblies' },
  { id: 'a2', label: 'Duplex receptacle — rough+trim', detail: '0.40 hr/ea · costs from your library', group: 'Assemblies' },
  { id: 'a3', label: 'Panel circuit — homerun', detail: '1.25 hr/ea · costs from your library', group: 'Assemblies' },
];

/**
 * What picking a catalog item fills into the row (unit + the contractor's own
 * cost; assemblies carry suggested hours but NO cost until the library has one).
 */
export const CATALOG_DEFAULTS: Record<string, { unit: string; unitCost: number | null }> = {
  lr1: { unit: 'hr', unitCost: 78 },
  lr2: { unit: 'hr', unitCost: 52 },
  lr3: { unit: 'hr', unitCost: 94.5 },
  m1: { unit: 'ft', unitCost: 0.48 },
  m2: { unit: 'ft', unitCost: 0.72 },
  m3: { unit: 'ea', unitCost: 3.1 },
  m4: { unit: 'ea', unitCost: 0.85 },
  m5: { unit: 'ea', unitCost: 1.6 },
  a1: { unit: 'ea', unitCost: null },
  a2: { unit: 'ea', unitCost: null },
  a3: { unit: 'ea', unitCost: null },
};

export function searchCatalog(query: string): LineGridSuggestion[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return CATALOG;
  return CATALOG.filter((s) => s.label.toLowerCase().includes(q));
}

export interface SampleEstimate {
  id: string;
  number: string;
  title: string;
  client: string;
  status: string;
  total: number;
  sentAt: string | null;
}

export const SAMPLE_ESTIMATES: SampleEstimate[] = [
  {
    id: 'e1',
    number: 'E-20260718-004',
    title: 'Maple St kitchen remodel',
    client: 'Hendricks Residence',
    status: 'sent',
    total: 18420,
    sentAt: '2026-07-18',
  },
  {
    id: 'e2',
    number: 'E-20260715-002',
    title: 'Cedar Ave service upgrade',
    client: 'T. Okafor',
    status: 'accepted',
    total: 4980,
    sentAt: '2026-07-15',
  },
  {
    id: 'e3',
    number: 'E-20260714-001',
    title: 'Northfield office TI — lighting',
    client: 'Brightline Properties',
    status: 'viewed',
    total: 27350,
    sentAt: '2026-07-14',
  },
  {
    id: 'e4',
    number: 'E-20260710-003',
    title: 'Lakeside deck wiring',
    client: 'M. Virtanen',
    status: 'declined',
    total: 2140,
    sentAt: '2026-07-10',
  },
  {
    id: 'e5',
    number: 'E-20260709-001',
    title: 'Willow Ct EV charger',
    client: 'D. Sandoval',
    status: 'draft',
    total: 1860,
    sentAt: null,
  },
];

export const ALL_STATUSES = [
  'draft',
  'sent',
  'viewed',
  'accepted',
  'declined',
  'expired',
  'superseded',
  'open',
  'awarded',
  'invited',
  'active',
  'revoked',
  'submitted',
  'withdrawn',
  'verified',
  'unverified_draft',
  'federal_anchor',
];
